/**
 * Display configuration for Details Panel
 * Defines field order, labels, and rendering components for island content
 */

import { IslandHeaders, FactSheetFields } from "@/types/index.ts";

/**
 * Minimum number of required features for the National and/or Indigenous Features and Characteristics field
 */
export const MINIMUM_REQUIRED_FEATURES = 3;

/**
 * Default values for National and/or Indigenous Features and Characteristics field
 */
export const DEFAULT_NATIONAL_INDIGENOUS_FEATURES = ["", "", ""];

/**
 * Island Display Configuration
 * Defines the order and presentation of island fields
 * Combines the island name from the Islands CSV with all fact sheet fields
 * that students populate for their assigned islands
 */
export const ISLAND_DISPLAY_CONFIG = [
  { field: IslandHeaders.NAME, label: null, component: "title" },
  {
    field: FactSheetFields.WESTERN_NAME,
    label: FactSheetFields.WESTERN_NAME,
    component: "field"
  },
  {
    field: FactSheetFields.INDIGENOUS_NAME,
    label: FactSheetFields.INDIGENOUS_NAME,
    component: "field"
  },
  {
    field: FactSheetFields.INDIGENOUS_LANGUAGE,
    label: `${FactSheetFields.INDIGENOUS_LANGUAGE}(s)`,
    component: "field"
  },
  {
    field: FactSheetFields.PRIMARY_LANGUAGE,
    label: `${FactSheetFields.PRIMARY_LANGUAGE}(s)`,
    component: "field"
  },
  {
    field: FactSheetFields.POPULATION_SIZE,
    label: FactSheetFields.POPULATION_SIZE,
    component: "field"
  },
  {
    field: FactSheetFields.CULTURAL_REGION,
    label: FactSheetFields.CULTURAL_REGION,
    component: "field"
  },
  {
    field: FactSheetFields.CAPITAL,
    label: FactSheetFields.CAPITAL,
    component: "field"
  },
  {
    field: FactSheetFields.POLITICAL_STRUCTURE,
    label: FactSheetFields.POLITICAL_STRUCTURE,
    component: "section"
  },
  {
    field:
      FactSheetFields.NATIONAL_AND_OR_INDIGENOUS_FEATURES_AND_CHARACTERISTICS,
    label: `${FactSheetFields.NATIONAL_AND_OR_INDIGENOUS_FEATURES_AND_CHARACTERISTICS} (3 or more)`,
    component: "section"
  }
];

/**
 * Get display configuration for a given content type
 * @param {string} type - Content type (currently only "island" is supported)
 * @returns {Array} Display configuration array
 */
export function getDisplayConfig(type) {
  switch (type) {
    case "island":
      return ISLAND_DISPLAY_CONFIG;
    default:
      return [];
  }
}

/**
 * Gets the maximum possible score for a report
 * Returns the total number of fact sheet fields that can be filled
 * @returns {number} The maximum score (total number of fact sheet fields)
 */
export function getMaxScore() {
  // Count all fields in the FactSheetFields enum
  return Object.keys(FactSheetFields).length;
}

/**
 * Counts the number of valid inputs in a ReportDetails object
 * A field is considered valid if it has a non-empty string value
 * The National and/or Indigenous Features and Characteristics field counts as 1
 * only if it has at least 3 non-empty items in the array
 * @param {Object} reportDetails - The ReportDetails object to count valid inputs for
 * @returns {number} The number of valid inputs
 */
export function countValidInputs(reportDetails) {
  if (!reportDetails || typeof reportDetails !== "object") {
    return 0;
  }

  let count = 0;

  // Count all string fields that have non-empty values
  Object.keys(reportDetails).forEach((key) => {
    const value = reportDetails[key];

    if (isFieldValid(key, value)) {
      count++;
    }
  });

  return count;
}

/**
 * Checks if a field is valid
 * @param {string} key - The field key
 * @param {string|Array} value - The field value
 * @returns {boolean} True if the field is valid, false otherwise
 */
export function isFieldValid(key, value) {
  if (
    key !==
    FactSheetFields.NATIONAL_AND_OR_INDIGENOUS_FEATURES_AND_CHARACTERISTICS
  ) {
    // Count string fields that have non-empty values
    if (typeof value === "string" && value.trim().length > 0) {
      return true;
    }
  } else if (Array.isArray(value)) {
    // Count non-empty items
    const validItems = value.filter(
      (item) => item && typeof item === "string" && item.trim().length > 0
    );
    // Only count as 1 if there are at least 3 valid items
    if (validItems.length >= DEFAULT_NATIONAL_INDIGENOUS_FEATURES.length) {
      return true;
    }
  }
  return false;
}

/**
 * Gets the missing fields in a ReportDetails object
 * @param {Object} reportDetails - The ReportDetails object to get missing fields for
 * @returns {Array} An array of missing fields
 */
export function getMissingFields(reportDetails) {
  return Object.values(FactSheetFields).filter(
    (key) => !isFieldValid(key, reportDetails[key])
  );
}
